<template>
  <validation-observer ref="simpleRules">
    <b-card>
      <b-form @submit.prevent>
        <b-row>
          <b-col
            cols="12"
            lg="6"
            class="mb-2"
          >
            <b-card class="border h-100 mb-0">
              <div class="d-flex align-items-center mb-2">
                <feather-icon
                  icon="UserIcon"
                  size="18"
                  class="mr-50 text-primary"
                />
                <h5 class="mb-0">
                  {{ $t('accountMembers.sections.generalInformation') }}
                </h5>
              </div>

              <b-row>
                <b-col
                  cols="12"
                  md="6"
                >
                  <b-form-group
                    :label="$t('accountMembers.fields.username')"
                    label-for="mc-username"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Username"
                      rules="required"
                    >
                      <b-form-input
                        id="mc-username"
                        v-model="form.username"
                        :placeholder="$t('accountMembers.placeholders.username')"
                        disabled
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col
                  cols="12"
                  md="6"
                >
                  <b-form-group
                    :label="$t('accountMembers.fields.nickname')"
                    label-for="mc-nickname"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Nickname"
                      rules="required"
                    >
                      <b-form-input
                        id="mc-nickname"
                        v-model="form.nickName"
                        :placeholder="$t('accountMembers.placeholders.nickname')"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col
                  cols="12"
                  md="6"
                >
                  <b-form-group
                    :label="$t('accountMembers.fields.password')"
                    label-for="change-password"
                  >
                    <b-button
                      v-ripple.400="'var(--hdg-ripple-primary)'"
                      type="button"
                      variant="outline-primary"
                      block
                      @click.prevent="modalPassword = true"
                    >
                      <feather-icon
                        icon="LockIcon"
                        size="14"
                        class="mr-50"
                      />
                      {{ $t('Change Password') }}
                    </b-button>
                  </b-form-group>
                </b-col>

                <b-col
                  cols="12"
                  md="6"
                >
                  <b-form-group
                    :label="$t('accountMembers.fields.phone')"
                    label-for="mc-phone"
                    class="mb-0"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Phone"
                      rules="required|digits:10"
                    >
                      <b-form-input
                        id="mc-phone"
                        v-model="form.phone"
                        :placeholder="$t('accountMembers.placeholders.phone')"
                        minlength="10"
                        maxlength="10"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>
          </b-col>

          <b-col
            cols="12"
            lg="6"
            class="mb-2"
          >
            <b-card class="border h-100 mb-0">
              <div class="d-flex align-items-center mb-2">
                <feather-icon
                  icon="SettingsIcon"
                  size="18"
                  class="mr-50 text-primary"
                />
                <h5 class="mb-0">
                  {{ $t('accountMembers.sections.features') }}
                </h5>
              </div>

              <b-row>
                <b-col
                  v-if="$can('update', 'poy')"
                  cols="6"
                >
                  <div class="feature-item">
                    <span class="feature-label">{{ $t('accountMembers.features.sendPoy') }}</span>
                    <b-form-checkbox
                      v-model="form.canSendPoy"
                      switch
                      class="ml-auto"
                    />
                  </div>
                </b-col>

                <b-col
                  v-if="$can('update', 'agent')"
                  cols="6"
                >
                  <div class="feature-item">
                    <span class="feature-label">{{ $t('accountMembers.features.createAgent') }}</span>
                    <b-form-checkbox
                      v-model="form.canCreateAgent"
                      switch
                      class="ml-auto"
                    />
                  </div>
                </b-col>

                <b-col
                  v-if="$can('update', 'poy')"
                  cols="6"
                >
                  <div class="feature-item">
                    <span class="feature-label">{{ $t('accountMembers.features.webApi') }}</span>
                    <b-form-checkbox
                      v-model="form.canWebApi"
                      switch
                      class="ml-auto"
                    />
                  </div>
                </b-col>

                <b-col
                  v-if="$can('update', 'payRate')"
                  cols="6"
                >
                  <div class="feature-item">
                    <span class="feature-label">{{ $t('accountMembers.features.customRate') }}</span>
                    <b-form-checkbox
                      v-model="form.canCustomPayRate"
                      switch
                      class="ml-auto"
                    />
                  </div>
                </b-col>
                <b-col
                  v-if="canManagePoyDetails"
                  cols="6"
                >
                  <div class="feature-item">
                    <span class="feature-label">{{ $t('accountMembers.features.poyDetailsCallback') }}</span>
                    <b-form-checkbox
                      v-model="form.canPoyDetailsCallback"
                      :aria-label="$t('accountMembers.features.poyDetailsCallback')"
                      switch
                      class="ml-auto"
                    />
                  </div>
                </b-col>

                <b-col
                  v-if="canManagePoyDetails"
                  cols="6"
                >
                  <div class="feature-item">
                    <span class="feature-label">{{ $t('accountMembers.features.poyDetailsApi') }}</span>
                    <b-form-checkbox
                      v-model="form.canPoyDetailsApi"
                      :aria-label="$t('accountMembers.features.poyDetailsApi')"
                      switch
                      class="ml-auto"
                    />
                  </div>
                </b-col>
              </b-row>
            </b-card>
          </b-col>

          <b-col
            cols="12"
            lg="6"
            class="mb-2"
          >
            <b-card class="border h-100 mb-0">
              <div class="d-flex align-items-center mb-2">
                <feather-icon
                  icon="CreditCardIcon"
                  size="18"
                  class="mr-50 text-primary"
                />
                <h5 class="mb-0">
                  {{ $t('accountMembers.sections.creditSetting') }}
                </h5>
              </div>

              <b-form-group
                :label="$t('accountMembers.fields.walletType')"
                label-for="wallet-type"
              >
                <b-form-input
                  id="wallet-type"
                  :value="form.walletType === 1 ? $t('accountMembers.walletTypes.transfer') : $t('accountMembers.walletTypes.single')"
                  disabled
                />
              </b-form-group>

              <b-row v-if="form.walletType === 0">
                <b-col
                  cols="12"
                  md="6"
                >
                  <b-form-group
                    :label="$t('accountMembers.fields.ourPt')"
                    label-for="our-pt"
                  >
                    <b-form-input
                      id="mc-our-pt"
                      v-model="ourPercent"
                      placeholder="our percent"
                      disabled
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  cols="12"
                  md="6"
                >
                  <b-form-group
                    :label="$t('accountMembers.fields.givenPt')"
                    label-for="given-pt"
                  >
                    <b-input-group>
                      <b-form-input
                        id="given-pt"
                        v-model="form.percent"
                        disabled
                        type="number"
                      />
                      <b-input-group-append>
                        <b-button
                          v-ripple.400="'var(--hdg-ripple-primary)'"
                          variant="outline-primary"
                          @click.prevent="showPercentForm"
                        >
                          <feather-icon
                            icon="EditIcon"
                            size="14"
                          />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-form-group
                v-if="form.walletType === 1"
                :label="$t('accountMembers.fields.credit')"
                label-for="mc-pt"
              >
                <b-form-input
                  id="mc-pt"
                  v-model="form.credit"
                  type="number"
                  disabled
                  :placeholder="$t('accountMembers.fields.credit')"
                />
              </b-form-group>

              <b-form-group
                v-if="$can('update', 'poy') && form.canSendPoy"
                :label="$t('accountMembers.fields.creditPoy')"
                label-for="credit-poy"
                class="mb-0"
              >
                <b-input-group>
                  <b-form-input
                    id="credit-poy"
                    v-model="form.partnerCredit"
                    disabled
                    type="number"
                  />
                  <b-input-group-append>
                    <b-button
                      v-ripple.400="'var(--hdg-ripple-primary)'"
                      variant="outline-primary"
                      @click.prevent="showCreditPoyForm"
                    >
                      <feather-icon
                        icon="EditIcon"
                        size="14"
                      />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-card>
          </b-col>

          <b-col
            cols="12"
            lg="6"
            class="mb-2"
          >
            <b-card class="border h-100 mb-0">
              <div class="d-flex align-items-center mb-2">
                <feather-icon
                  icon="ToggleRightIcon"
                  size="18"
                  class="mr-50 text-primary"
                />
                <h5 class="mb-0">
                  {{ $t('Status') }}
                </h5>
              </div>

              <b-row>
                <b-col
                  cols="12"
                  md="6"
                >
                  <b-form-group
                    :label="$t('Suspend')"
                    label-for="suspend-status"
                    class="mb-md-0"
                  >
                    <b-form-input
                      id="suspend-status"
                      disabled
                      :value="isEnable === true ? $t('No') : $t('Yes')"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  cols="12"
                  md="6"
                  class="d-flex align-items-end"
                >
                  <b-button
                    v-ripple.400="'var(--hdg-ripple-primary)'"
                    type="button"
                    variant="outline-primary"
                    block
                    @click.prevent="submitStatus"
                  >
                    <feather-icon
                      icon="RefreshCwIcon"
                      size="14"
                      class="mr-50"
                    />
                    {{ $t('Edit Status') }}
                  </b-button>
                </b-col>
              </b-row>

              <b-form-group
                v-show="canUpdateWebApi"
                :label="$t('accountMembers.fields.ipWhitelist')"
                label-for="ip-whitelist"
                class="mt-2 mb-0"
              >
                <div
                  ref="whitelistForm"
                  class="ip-whitelist-inline"
                >
                  <div
                    v-for="(v,i) in form.ipWhiteList"
                    :key="i"
                    ref="row"
                    class="ip-whitelist-tag"
                  >
                    <b-input-group size="sm">
                      <b-form-input
                        v-model="form.ipWhiteList[i]"
                        :placeholder="$t('IP Address')"
                        style="width: 130px;"
                      />
                      <b-input-group-append>
                        <b-button
                          variant="outline-danger"
                          :disabled="form.ipWhiteList.length <= 1"
                          @click="removeItem(i)"
                        >
                          <feather-icon
                            icon="XIcon"
                            size="14"
                          />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </div>
                  <b-button
                    v-ripple.400="'var(--hdg-ripple-primary)'"
                    variant="outline-primary"
                    size="sm"
                    class="ip-add-btn"
                    @click="addItem()"
                  >
                    <feather-icon
                      icon="PlusIcon"
                      size="14"
                    />
                  </b-button>
                </div>
              </b-form-group>
            </b-card>
          </b-col>

          <b-col
            v-if="canUpdateWebApi"
            cols="12"
            lg="6"
            class="mb-2"
          >
            <b-card class="border h-100 mb-0">
              <div class="d-flex align-items-center mb-2">
                <feather-icon
                  icon="GlobeIcon"
                  size="18"
                  class="mr-50 text-primary"
                />
                <h5 class="mb-0">
                  {{ $t('accountMembers.sections.apiSettings') }}
                </h5>
              </div>

              <b-form-group
                :label="$t('accountMembers.fields.websiteUrl')"
                label-for="mc-website-url"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Website URL"
                  rules="required|url"
                >
                  <b-form-input
                    id="mc-website-url"
                    v-model="form.websiteUrl"
                    :placeholder="$t('accountMembers.fields.websiteUrl')"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group
                v-if="form.walletType === 0"
                :label="$t('accountMembers.fields.callbackUrl')"
                label-for="mc-callback-url"
                class="mb-0"
              >
                <validation-provider
                  #default="{ errors }"
                  name="CallBack URL"
                  rules="required|url"
                >
                  <b-form-input
                    id="mc-callback-url"
                    v-model="form.callbackUrl"
                    :placeholder="$t('accountMembers.fields.callbackUrl')"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-card>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            cols="12"
            class="text-right"
          >
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="primary"
              size="lg"
              class="d-inline-flex align-items-center"
              @click.prevent="onSubmitForm"
            >
              <feather-icon
                icon="SaveIcon"
                size="18"
                class="mr-50"
              />
              {{ $t('accountMembers.actions.submit') }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <b-modal
      v-model="modalPassword"
      cancel-variant="secondary"
      ok-only
      ok-title="Submit"
      centered
      title="เปลี่ยนรหัสผ่าน"
      button-size="sm"
      @ok="submitPassword"
    >
      <b-form @submit.prevent>
        <b-form-group
          label="Password"
          label-for="mc-password"
        >
          <validation-provider
            #default="{ errors }"
            name="Password"
            vid="Password"
            rules="required|min:8|password"
          >
            <b-form-input
              id="mc-password"
              v-model="passwordForm.password"
              autofocus
              placeholder="Password"
              :state="errors.length > 0 ? false:null"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group
          label="Confirm Password"
          label-for="mc-confirm-password"
        >
          <validation-provider
            #default="{ errors }"
            name="Confirm Password"
            rules="required|confirmed:Password"
          >
            <b-form-input
              id="mc-confirm-password"
              v-model="passwordForm.passwordConfirm"
              :state="errors.length > 0 ? false:null"
              placeholder="Confirm Password"
              @keydown.enter="submitPassword"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
      </b-form>
    </b-modal>

    <b-modal
      v-model="modalPercent"
      cancel-variant="secondary"
      ok-only
      ok-title="Submit"
      centered
      title="Edit Commission"
      button-size="sm"
      @ok="submitPercent"
    >
      <b-form @submit.prevent>
        <b-form-group
          label="Our PT(%)"
          label-for="our-pt"
          label-cols-md="4"
          class="mr-lg-3"
        >
          <b-form-input
            id="our-pt"
            v-model="percentForm.ourPercent"
            placeholder="our percent"
            disabled
          />
        </b-form-group>
        <b-form-group
          label="Given PT(%)"
          label-for="mc-pt"
          label-cols-md="4"
          class="mr-lg-3"
        >
          <validation-provider
            #default="{ errors }"
            name="Given PT(%)"
            rules="required|min_value:1|max_value:100"
          >
            <b-form-input
              id="mc-pt"
              v-model="percentForm.percent"
              type="number"
              placeholder="Given PT(%)"
              autofocus
              @input="finishInputPercent"
              @keydown.enter="submitPercent"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
      </b-form>
    </b-modal>

    <b-modal
      v-model="modalPoyCredit"
      cancel-variant="secondary"
      ok-only
      ok-title="Submit"
      centered
      title="Edit Credit Poy"
      button-size="sm"
      @ok="submitCreditPoy"
    >
      <b-form @submit.prevent>
        <b-form-group
          label="Credit Poy"
          label-for="mc-pt"
          label-cols-md="4"
          class="mr-lg-3"
        >
          <validation-provider
            #default="{ errors }"
            name="Credit"
            rules="decimal|min_value:0"
          >
            <b-form-input
              id="mc-pt"
              v-model="creditPoyForm.credit"
              type="number"
              placeholder="Credit"
              autofocus
              @keydown.enter="submitCreditPoy"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
      </b-form>
    </b-modal>
  </validation-observer>
</template>

<script>
/* eslint-disable */
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BForm,
  BButton,
  BModal,
  BInputGroup,
  BInputGroupAppend,
  BFormCheckbox,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {} from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { heightTransition } from '@core/mixins/ui/transition'
import agentService from '@/service/agentService'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BModal,
    BInputGroup,
    BInputGroupAppend,
    BFormCheckbox,
  },
  directives: {
    Ripple,
  },
  mixins: [heightTransition],
  computed: {
    canManagePoyDetails() {
      return this.$store.getters['app/canManagePoyDetails']
    },
    canUpdateWebApi() {
      return this.$can('update', 'api') && this.$can('update', 'poy') && this.form.canWebApi
    }
  },
  data() {
    return {
      percent: null,
      ourPercent: null,
      form: {
        canPoyDetailsCallback: false,
        canPoyDetailsApi: false,
      },
      isEnable: false,

      modalPassword: false,
      passwordForm: {
        password: '',
        passwordConfirm: '',
      },

      modalPercent: false,
      percentForm: {
        ourPercent: null,
        percent: null,
      },
      creditPoyForm: {
        credit: 0,
      },
      modalPoyCredit: false,
    }
  },
  created() {
    window.addEventListener('resize', this.initTrHeight)
  },
  destroyed() {
    window.removeEventListener('resize', this.initTrHeight)
  },
  async mounted() {
    const username = this.$router.currentRoute.query.id
    if (username !== null && username !== undefined) {
      const [res, res2] = await Promise.all([agentService.getAuthen(`find?username=${username}`), agentService.getAuthen(`percent`)])
      if (res.status === 200) {
        this.form = res.data
        this.form.canPoyDetailsCallback = res.data.canPoyDetailsCallback === true
        this.form.canPoyDetailsApi = res.data.canPoyDetailsApi === true
        this.isEnable = res.data.isEnable
        if (this.form.ipWhiteList === null) {
          this.form.ipWhiteList = []
        }
      }
      if (res2.status === 200) {
        if (res2.status === 200) {
          this.ourPercent = this.percent - this.form.percent
        } else {
          this.ourPercent = this.percent
        }
      }
    }

    this.initTrHeight()
  },
  methods: {
    initTrHeight() {
      this.trSetHeight(null)
      this.$nextTick(() => {
        this.trSetHeight(this.$refs.whitelistForm.scrollHeight)
      })
    },
    addItem() {
      this.form.ipWhiteList.push('')
      this.trAddHeight(this.$refs.row[0].offsetHeight)
    },
    removeItem(i) {
      if (this.form.ipWhiteList.length <= 1) return
      this.form.ipWhiteList.splice(i, 1)
      this.trTrimHeight(this.$refs.row[0].offsetHeight)
    },
    finishInputPercent() {
      const dt = this.form.percent - this.percentForm.percent
      this.percentForm.ourPercent = this.ourPercent + dt
    },
    onSubmitForm() {
      this.$refs.simpleRules.validate()
          .then(async success => {
            if (success) {
              this.$swal({
                dark: true,
                title: 'Are you sure?',
                text: 'คุณเเน่ใจว่าต้องการบันทึกข้อมูลใช่หรือไม่',
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Ok',
                customClass: {
                  confirmButton: 'btn btn-primary',
                  cancelButton: 'btn btn-outline-danger ml-1',
                },
                buttonsStyling: false,
              })
                  .then(async result => {
                    if (result.value) {
                      let checkValidIp = true
                      const listIp = []
                      for (let i = 0; i < this.form.ipWhiteList.length; i++) {
                        const ip = this.form.ipWhiteList[i]
                        if (ip !== '') {
                          checkValidIp = /^\d+\.\d+\.\d+\.\d+$/.test(ip)
                          listIp.push(ip)
                        }
                      }
                      if (checkValidIp === false) {
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: 'Error',
                            icon: 'AlertCircleIcon',
                            variant: 'danger',
                            text: 'please enter correct ip address',
                          },
                        })
                        return
                      }

                      this.form.ipWhiteList = listIp
                      this.initTrHeight()

                      const formSender = { ...this.form }
                      if (!this.canManagePoyDetails) {
                        delete formSender.canPoyDetailsCallback
                        delete formSender.canPoyDetailsApi
                      }
                      const res = await agentService.putAuthen(`?username=${this.form.username}`, formSender)
                      if (res.status === 200) {
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: 'success',
                            icon: 'CoffeeIcon',
                            variant: 'success',
                            text: res.data,
                          },
                        })
                      } else {
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: 'Error',
                            icon: 'AlertCircleIcon',
                            variant: 'danger',
                            text: res.data,
                          },
                        })
                      }
                    }
                  })
            }
          })
    },
    submitStatus() {
      this.$refs.simpleRules.validate()
          .then(async success => {
            if (success) {
              this.$swal({
                dark: true,
                title: 'Lock / Unlock Account',
                text: 'คุณเเน่ใจว่าต้องการดำเนินการใช่หรือไม่ ?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes',
                customClass: {
                  confirmButton: 'btn btn-primary',
                  cancelButton: 'btn btn-outline-danger ml-1',
                },
                buttonsStyling: false,
              })
                  .then(async result => {
                    if (result.value) {
                      const res = await agentService.putAuthen(`toggle-status?username=${this.form.username}`)
                      if (res.status === 200) {
                        this.isEnable = res.data
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: 'success',
                            icon: 'CoffeeIcon',
                            variant: 'success',
                            text: 'ดำเนินการสำเร็จ',
                          },
                        })
                      } else {
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: 'Error',
                            icon: 'AlertCircleIcon',
                            variant: 'danger',
                            text: res.data,
                          },
                        })
                      }
                    }
                  })
            }
          })
    },
    submitPassword(e) {
      e.preventDefault()
      this.$refs.simpleRules.validate()
          .then(async success => {
            if (success) {
              this.$swal({
                dark: true,
                title: 'Are you sure?',
                text: 'คุณเเน่ใจว่าต้องการเปลี่ยนรหัสผ่านใช่หรือไม่',
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Ok',
                customClass: {
                  confirmButton: 'btn btn-primary',
                  cancelButton: 'btn btn-outline-danger ml-1',
                },
                buttonsStyling: false,
              })
                  .then(async result => {
                    if (result.value) {
                      const formSender = { ...this.passwordForm }
                      delete formSender.passwordConfirm

                      const res = await agentService.putAuthen(`password?username=${this.form.username}`, formSender)
                      if (res.status === 200) {
                        this.modalPassword = false
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: 'success',
                            icon: 'CoffeeIcon',
                            variant: 'success',
                            text: res.data,
                          },
                        })
                      } else {
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: 'Error',
                            icon: 'AlertCircleIcon',
                            variant: 'danger',
                            text: res.data,
                          },
                        })
                      }
                    }
                  })
            }
          })
    },
    showPercentForm() {
      this.percentForm.ourPercent = this.ourPercent
      this.percentForm.percent = this.form.percent
      this.modalPercent = true
    },
    showCreditForm() {
      this.creditForm.credit = this.form.credit + ''
      this.modalCredit = true
    },
    showCreditPoyForm() {
      this.creditPoyForm.credit = this.form.partnerCredit + ''
      this.modalPoyCredit = true
    },
    submitPercent(e) {
      e.preventDefault()
      this.$refs.simpleRules.validate()
          .then(async success => {
            if (success) {
              this.$swal({
                dark: true,
                title: 'Are you sure?',
                text: 'คุณเเน่ใจว่าต้องการบันทึกข้อมูลใช่หรือไม่',
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Ok',
                customClass: {
                  confirmButton: 'btn btn-primary',
                  cancelButton: 'btn btn-outline-danger ml-1',
                },
                buttonsStyling: false,
              })
                  .then(async result => {
                    if (result.value) {
                      const percentForm = { ...this.percentForm }
                      delete percentForm.ourPercent

                      const res = await agentService.putAuthen(`edit-percent?username=${this.form.username}`, percentForm)
                      if (res.status === 200) {
                        this.modalPercent = false
                        this.ourPercent = this.percent - res.data
                        this.form.percent = res.data
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: 'success',
                            icon: 'CoffeeIcon',
                            variant: 'success',
                            text: 'บันทึกข้อมูลสำเร็จ',
                          },
                        })
                      } else {
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: 'Error',
                            icon: 'AlertCircleIcon',
                            variant: 'danger',
                            text: 'sorry , something wrong please try again',
                          },
                        })
                      }
                    }
                  })
            }
          })
    },
    submitCredit(e) {
      e.preventDefault()
      let keydownEvent = (e) => {
        if (e.key === 'Enter' || e.key === 'Space') {
          this.$swal.clickConfirm()
        } else if (e.key === 'Escape') {
          this.$swal.close()
        }
      }
      this.$refs.simpleRulesCredit.validate()
          .then(async success => {
            if (success) {
              this.$swal({
                dark: true,
                title: 'Are you sure?',
                text: `คุณต้องการอัพเดตเครดิตเป็น ${this.creditForm.credit} ใช่หรือไม่`,
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Ok',
                customClass: {
                  confirmButton: 'btn btn-primary',
                  cancelButton: 'btn btn-outline-danger ml-1',
                },
                buttonsStyling: false,
                allowEnterKey: true,
                focusConfirm: true,
                inputAutoFocus: true,
                onOpen: () => {
                  document.addEventListener('keydown', keydownEvent)
                },
                onClose: () => {
                  document.removeEventListener('keydown', keydownEvent)
                }
              })
                  .then(async result => {
                    if (result.value) {
                      const form = {
                        credit: parseFloat(this.creditForm.credit)
                      }

                      const res = await agentService.putAuthen(`edit-credit?username=${this.form.username}`, form)
                      if (res.status === 200) {
                        this.modalCredit = false
                        this.form.credit = res.data.agentCredit + ''
                        this.creditSetting.credit = `${res.data.ownerCredit}`
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: 'success',
                            icon: 'CoffeeIcon',
                            variant: 'success',
                            text: 'ดำเนินการสำเร็จ',
                          },
                        })
                      } else {
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: 'Error',
                            icon: 'AlertCircleIcon',
                            variant: 'danger',
                            text: 'sorry , something wrong please try again',
                          },
                        })
                      }
                    }
                  })
            }
          })
    },
    submitCreditPoy(e) {
      e.preventDefault()
      let keydownEvent = (e) => {
        if (e.key === 'Enter' || e.key === 'Space') {
          this.$swal.clickConfirm()
        } else if (e.key === 'Escape') {
          this.$swal.close()
        }
      }
      this.$refs.simpleRules.validate()
          .then(async success => {
            if (success) {
              this.$swal({
                dark: true,
                title: 'Are you sure?',
                text: `คุณต้องการอัพเดตเครดิตเป็น ${this.creditPoyForm.credit} ใช่หรือไม่`,
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Ok',
                customClass: {
                  confirmButton: 'btn btn-primary',
                  cancelButton: 'btn btn-outline-danger ml-1',
                },
                buttonsStyling: false,
                allowEnterKey: true,
                focusConfirm: true,
                inputAutoFocus: true,
                onOpen: () => {
                  document.addEventListener('keydown', keydownEvent)
                },
                onClose: () => {
                  document.removeEventListener('keydown', keydownEvent)
                }
              })
                  .then(async result => {
                    if (result.value) {
                      const form = {
                        credit: parseFloat(this.creditPoyForm.credit)
                      }

                      const res = await agentService.putAuthen(`edit-credit-poy?username=${this.form.username}`, form)
                      if (res.status === 200) {
                        this.modalPoyCredit = false
                        this.form.partnerCredit = res.data
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: 'success',
                            icon: 'CoffeeIcon',
                            variant: 'success',
                            text: 'อัพเดตเครดิตสำเร็จ',
                          },
                        })
                      } else {
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: 'Error',
                            icon: 'AlertCircleIcon',
                            variant: 'danger',
                            text: 'sorry , something wrong please try again',
                          },
                        })
                      }
                    }
                  })
            }
          })
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';

.repeater-form {
  overflow: hidden;
  transition: .35s height !important;
}

.inline-spacing {
  display: flex;
  flex-wrap: wrap;
  text-align: end;
  justify-content: flex-start;
  align-items: center;

  & > * {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    //margin-left: 1.5rem;
  }
}

.feature-item {
  display: flex;
  align-items: center;
  min-height: 42px;
  padding: 0.5rem 0;
}

.feature-label {
  color: var(--body-color);
  font-weight: 500;
}

.ip-whitelist-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: flex-start;

  .ip-whitelist-tag {
    min-width: 175px;
  }

  .ip-add-btn {
    min-width: 38px;
  }
}
</style>
